import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useLocation, withRouter } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import PageTitle from 'components/PageTitle';
import ScrollToTop from 'components/ScrollToTop';
import Producer from 'scenes/Producer';
import SelectAccount from 'v2/components/Pages/Authentication/SelectAccount';
import BankAccountConfirmation from 'v2/components/Pages/BankAccountConfirmation';
import BankAccountForRefund from 'v2/components/Pages/BankAccountForRefund';
import ChangePaymentMethod from 'v2/components/Pages/ChangePaymentMethod';
import InviteAffiliate from 'v2/components/Pages/InviteAffiliate';
import InviteNewUser from 'v2/components/Pages/InviteNewUser';
import ResolveRecurringDateChange from 'v2/components/Pages/SubscriptionRecurringDate/ResolveChange';
import { SubscriptionWhatsappPayment } from 'v2/components/Pages/SubscriptionWhatsappPayment';
import { UnsubscribeWhatsappMessage } from 'v2/components/Pages/UnsubscribeWhatsappMessage';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import { useQuery } from 'v2/hooks/useQuery';
import authService from 'v2/services/auth';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import Confirmation from './Confirmation';
import Downloads from './Downloads';
import NotFound from './NotFound';
import ProviderValidation from './ProviderValidation';
import ContentAccessConfirmation from '../v2/components/Pages/ContentAccessConfirmation';

const Routes = () => {
  const query = useQuery();

  useEffect(() => {
    const chatBotId = query.get('chatbot');

    if (chatBotId) {
      window.sessionStorage.setItem('chatBotId', chatBotId);
    }
  }, [query]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageTitle />
      <Switch>
        <Redirect path='/redirect/:hash?/:page*' to='/' />
        <Redirect path='/register' to='/' />
        <Redirect path='/login' to='/' />
        <Redirect from='/cancel-subscription/:id/:token' to='/producer/shopping' />
        <Route path='/confirmation/coproducer' component={Confirmation} />
        <Route path='/recurring-date/resolve-change' component={ResolveRecurringDateChange} />
        <PrivateRoute path='/connect/validate/:provider'>
          <ProviderValidation />
        </PrivateRoute>
        <Route path='/downloads/:hash' component={Downloads} />
        <Route path='/content-access-confirmation/:hash' component={ContentAccessConfirmation} />
        <Route path='/bank-account-confirmation/:uuid' component={BankAccountConfirmation} />
        <Route path='/bank-account-refund' component={BankAccountForRefund} />
        <Route path='/change-payment-method' component={ChangePaymentMethod} />
        <Route path='/subscription-whatsapp-payment' component={SubscriptionWhatsappPayment} />
        <Route path='/unsubscribe-whatsapp-message' component={UnsubscribeWhatsappMessage} />
        <PrivateRoute path='/referred/:producerId?'>
          <InviteNewUser />
        </PrivateRoute>
        <PrivateRoute path='/affiliate/invite/:productId/:affiliateId?' canBePublic={true}>
          <InviteAffiliate />
        </PrivateRoute>
        <PrivateRoute path='/select-account'>
          <SelectAccount />
        </PrivateRoute>
        <PrivateRoute path='/'>
          <Producer />
        </PrivateRoute>
        <Route path='*' component={NotFound} />
      </Switch>
    </BrowserRouter>
  );
};

const PrivateRoute = React.memo(
  ({ canBePublic, children, ...rest }: { [key: string]: any; canBePublic?: boolean; children: React.ReactNode }) => {
    const [isAuthenticated] = useObservable(() => authService.isAuthenticated(), []);
    const [accesses] = useObservable(() => eduzzAccountsService.getAccesses(), []);
    const [isLogged] = useObservable(() => eduzzAccountsService.getIsLogged(), []);
    const [loadingAccount] = useObservable(() => eduzzAccountsService.getLoadingAccount(), []);
    const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
    const [permissions] = useObservable(() => eduzzAccountsService.getPermissions(), []);
    const hasSession = localStorage.getItem('ssid');
    const location = useLocation().search;

    const query = useMemo(() => new URLSearchParams(location), [location]);

    const reallyCanBePublic = useMemo(() => {
      const login = Boolean(query.get('login')) ?? false;

      if (login) {
        return false;
      }

      return canBePublic;
    }, [canBePublic, query]);

    useEffect(() => {
      if (eduzzAccountsService.isInitialized()) {
        return;
      }

      const register = Boolean(query.get('register')) ?? false;

      if (isAuthenticated) {
        return;
      }

      if (reallyCanBePublic && !hasSession) {
        return;
      }

      eduzzAccountsService.initialize(register);
    }, [reallyCanBePublic, hasSession, isAuthenticated, query]);

    const memoizedRender = useMemo(() => {
      return () => {
        if (reallyCanBePublic) {
          return children;
        }

        if (accesses?.length > 1 && !isLogged && !loadingAccount) {
          return <SelectAccount />;
        }

        if (loadingAccount || !isAuthenticated || !user || !permissions || !isLogged) {
          return <GlobalLoading />;
        }

        return children;
      };
    }, [reallyCanBePublic, accesses, children, isLogged, loadingAccount, isAuthenticated, user, permissions]);

    return <Route {...rest} render={memoizedRender} />;
  }
);

export default withRouter(Routes);
